function formatParams(obj: any = {}) {
  return Object.keys(obj)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    .join("&");
}

export const imageupload = function () {
  return {
    "access-token": window.localStorage.getItem("assets-token-web"),
    "app-code": "WEB",
  };
};

export function getUrl(params: any = undefined) {
  let url = "/api/cmdb-template/oss/file/upload-file";
  if (import.meta.env.MODE === "development") {
    url = "/dev-api" + url;
  }

  if (params) {
    const queryString = formatParams(params);
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  return url;
}

export function getUrl2(params: any = undefined) {
  let url = "/api/cmdb-template/oss/file/upload";
  if (import.meta.env.MODE === "development") {
    url = "/dev-api" + url;
  }

  if (params) {
    const queryString = formatParams(params);
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  return url;
}

/* -------------------------------------------------------------------------
 * Attachment value normalisation.
 *
 * A mupload field value is stored verbatim inside the model row in Mongo, so four
 * historical shapes coexist across projects:
 *   1. a bare URL string            (oldest imports)
 *   2. { previewUrl, preview_url }  (what the backend's FILE_ARRAY "历史数据处理方案"
 *                                    converter turns shape 1 into on the read path)
 *   3. { previewUrl, preview_url, status, uid }
 *   4. the full element-plus UploadFile + upload response (current)
 * Shapes 1-3 carry no `name` and no `type`, which is why they rendered as a generic
 * document icon and — because the only click handler sits on an <a> whose content is
 * {{ file.name }}, and element-plus styles it inline-flex — collapsed to 0px so nothing
 * on the row was clickable at all. That is the "上传的报告无法查看" report.
 * ------------------------------------------------------------------------- */
import { getToken, withQuery, isOwnPreviewUrl, isImage } from "@/utils/common";

const VIDEO_EXTS = ["mp4", "mov", "avi", "webm", "mkv", "m4v"];

// The retired stack is http-only, so an <img> from an https page is auto-upgraded and
// blocked. yhydn.imles.cn fronts the same legacy service and the same OSS bucket over
// https and resolves the same hashes, so this is a pure display-time rewrite. The raw
// value is kept in rawPreviewUrl and that is what gets persisted.
const LEGACY_HOST_REWRITES: Array<[RegExp, string]> = [
  [/^http:\/\/101\.200\.56\.114:8585\//i, "https://yhydn.imles.cn/"],
  [/^http:\/\/yhydn\.imles\.cn\//i, "https://yhydn.imles.cn/"],
];

function rewriteLegacyHost(u: string) {
  return LEGACY_HOST_REWRITES.reduce((acc, [re, to]) => acc.replace(re, to), u || "");
}

function extOf(s: string) {
  const path = String(s || "").split(/[?#]/)[0];
  const last = path.slice(path.lastIndexOf("/") + 1);
  const dot = last.lastIndexOf(".");
  return dot > 0 ? last.slice(dot + 1).toLowerCase() : "";
}

// blob:, data:, empty, and anything that already carries a token are all unusable.
function isUsableUrl(u: any) {
  return typeof u === "string"
    && (u.startsWith("/") || /^https?:\/\//i.test(u))
    && !/[?&]access-token=/.test(u);
}

export type ViewFile = {
  id?: string;
  name: string;
  type: string;
  size?: any;
  previewUrl: string;     // host-rewritten, display only
  rawPreviewUrl: string;  // exactly as stored — this is what gets saved back
  url: string;            // browser-loadable <img> src
  kind: "image" | "video" | "file" | "unknown";
  ocrFlag?: boolean;
};

/** Map any stored shape to one canonical view object. Never mutates the input. */
export function toViewFile(raw: any, index = 0): ViewFile | null {
  if (raw === null || raw === undefined) return null;
  const src: any = typeof raw === "string" ? { previewUrl: raw } : raw;

  const rawPreviewUrl = src.previewUrl || src.preview_url || "";
  const previewUrl = rewriteLegacyHost(rawPreviewUrl);

  let type = String(src.type || src.fileType || "").replace(/^\./, "").toLowerCase();
  if (!type) type = extOf(src.name || src.fileName || "") || extOf(previewUrl);

  const name = src.name || src.fileName || (type ? `附件${index + 1}.${type}` : `附件${index + 1}`);

  let url = "";
  if (previewUrl) {
    url = isOwnPreviewUrl(previewUrl)
      ? withQuery(previewUrl, { download: true, thumbnail: true, "access-token": getToken() })
      : previewUrl;                       // legacy host: no params, and never a token
  } else if (isUsableUrl(src.url)) {
    url = src.url;
  }

  // Deliberately NOT guessing "jpg" when the type is unknown: this field also holds PDFs,
  // and a guessed image type would put a PDF into an <el-image> modal instead of a working
  // new tab. "unknown" renders optimistically as an <img> with an @error fallback, and
  // opens via previewFile() so the browser decides.
  const kind = isImage(type) ? "image"
    : VIDEO_EXTS.includes(type) ? "video"
    : type ? "file" : "unknown";

  return { id: src.id, name, type, size: src.size ?? src.fileSize, previewUrl, rawPreviewUrl, url, kind, ocrFlag: src.ocrFlag };
}

/**
 * Full-size URL for the preview dialog / new tab.
 *
 * `download` maps straight onto the backend's flag, documented as
 * "true=返回文件流,false=转发到预览地址". Keep it true when the bytes are consumed by an <img>,
 * <video> or <el-image>; pass false when handing the url to the browser as a page, or it lands
 * on about:blank with a save prompt instead of the online viewer.
 */
export function signedViewUrl(f: any, opts: { download?: boolean } = {}) {
  if (!f?.previewUrl) return f?.url || "";
  const download = opts.download !== false;
  return isOwnPreviewUrl(f.previewUrl)
    ? withQuery(f.previewUrl, { download, "access-token": getToken() })
    : f.previewUrl;
}

// The ONLY keys we are willing to write back into the clinical record. Everything else
// element-plus hangs off an UploadFile (raw, uid, status, percentage, response and the
// transient blob: url) stays out of Mongo.
const STORED_KEYS = ["id", "name", "type", "size", "ocrFlag"] as const;

export function toStoredFile(f: any) {
  const out: any = {};
  for (const k of STORED_KEYS) if (f?.[k] !== undefined && f?.[k] !== null && f?.[k] !== "") out[k] = f[k];
  // persist the ORIGINAL url, never the display-time host rewrite
  const pu = f?.rawPreviewUrl || f?.previewUrl || f?.preview_url;
  if (pu) out.previewUrl = pu;
  return out;
}
